import { z } from "zod";
import {
  emailSchema,
  longTextSchema,
  optionalText,
  phoneSchema,
  reasonSchema,
  shortTextSchema,
  ulidSchema,
} from "./primitives.js";

/**
 * How old someone is at the event, in completed years.
 *
 * An age rather than a birth date, everywhere one is asked for. It is the answer the bands actually
 * need (D18), it is the one a person can give at a desk without producing a document, and it is
 * the smaller thing to hold about a child. Coerced from a string because that is what a number
 * input submits, and bounded so a mistyped year lands as a validation error rather than a dancer
 * in no band at all.
 */
export const ageSchema = z.coerce.number().int().min(0).max(120);

/** The same, tolerant of the empty string a blank number input submits. */
export const optionalAge = z.preprocess(
  (value) => (value === "" || value === null ? undefined : value),
  ageSchema.optional(),
);

/**
 * How many numbers a desk may issue, and how many desks there may be (D86).
 *
 * Defined here rather than beside the allocator in `@powwow/domain` only because of which way the
 * packages depend: domain imports validation, so this is the lower of the two and the one both can
 * see. The reasoning for the scheme is with the allocator, in `ids.ts`.
 *
 * Nine desks, and **9,999 numbers each**. The first draft gave each desk a thousand, which is past
 * anything the pilot will see and not past what exists — the largest pow wows in North America run
 * to several thousand dancers, and a ceiling that is only reached by the biggest events is a ceiling
 * that fails on the most important day somebody has. Ten thousand each puts every number inside five
 * digits and the whole event inside 99,999, which is the real bound: a number a caller reads aloud
 * and a registrar reads off a slip.
 */
export const STATION_BLOCK_SIZE = 10_000;
export const MAX_STATION = 9;

/** The most numbers one event can issue, across every desk and the public form. */
export const MAX_EVENT_NUMBER = 99_999;

/**
 * Which desk a registration came through.
 *
 * Bounded to the same 1–`MAX_STATION` the sequencer derives bands for, and refused here rather than
 * thrown from the allocator, so a mistyped station is a validation message at the desk that set it
 * instead of an error in front of the next person in the queue.
 */
export const stationSchema = z.coerce.number().int().min(1).max(MAX_STATION);

/**
 * The machine sending this, so two set to the same desk can be noticed.
 *
 * Deliberately lax — a bounded string rather than the ULID it will in practice be. It exists only to
 * tell one browser from another, and refusing a registration because a device's stored id was
 * written by an older version, or by a kiosk profile that hands out a fresh one, would trade a
 * warning nobody asked for against the registration in front of somebody.
 */
export const deviceIdSchema = z.string().min(1).max(64);

export const participantSchema = z.object({
  firstName: shortTextSchema,
  lastName: shortTextSchema,
  preferredName: optionalText(shortTextSchema),
  publicDisplayName: optionalText(shortTextSchema),
  communityAffiliation: optionalText(shortTextSchema),
  phone: optionalText(phoneSchema),
  /** Optional throughout. Every contact on the pilot poster is a phone number (D21). */
  email: optionalText(emailSchema),
  gender: optionalText(shortTextSchema),
  address: optionalText(shortTextSchema),
  city: optionalText(shortTextSchema),
  region: optionalText(shortTextSchema),
  postalCode: optionalText(shortTextSchema),
  country: optionalText(shortTextSchema),
  guardianName: optionalText(shortTextSchema),
  guardianPhone: optionalText(phoneSchema),
  emergencyContactName: optionalText(shortTextSchema),
  emergencyContactPhone: optionalText(phoneSchema),
  notes: optionalText(longTextSchema),
});

/**
 * A field a correction may clear.
 *
 * The create schema's `optionalText` folds `""` and `null` to undefined, which is right on the way
 * in — a blank box on a registration form was never answered. On a patch it is wrong twice over: it
 * throws away the difference between "leave last year's guardian alone" and "there is no guardian
 * any more", and it leaves fields with no empty-string tolerance rejecting the `null` a dialog
 * sends for an empty box. So here blank means null, null survives the parse, and `participantPatch`
 * gets the instruction it already knows how to act on.
 */
function clearable<T extends z.ZodType<string>>(schema: T) {
  return z.preprocess((value) => (value === "" ? null : value), schema.nullable().optional());
}

/**
 * A staff correction to a participant. Every field optional — a dialog sends what it holds — and
 * every field but the names clearable, because a correction includes deleting something that was
 * never true.
 */
export const updateParticipantSchema = z.object({
  firstName: shortTextSchema.optional(),
  lastName: shortTextSchema.optional(),
  preferredName: clearable(shortTextSchema),
  publicDisplayName: clearable(shortTextSchema),
  communityAffiliation: clearable(shortTextSchema),
  phone: clearable(phoneSchema),
  email: clearable(emailSchema),
  gender: clearable(shortTextSchema),
  address: clearable(shortTextSchema),
  city: clearable(shortTextSchema),
  region: clearable(shortTextSchema),
  postalCode: clearable(shortTextSchema),
  country: clearable(shortTextSchema),
  guardianName: clearable(shortTextSchema),
  guardianPhone: clearable(phoneSchema),
  emergencyContactName: clearable(shortTextSchema),
  emergencyContactPhone: clearable(phoneSchema),
  notes: clearable(longTextSchema),
});

/**
 * A staff correction to one team member's registration (D57): their identity, their form answers, or
 * both. Everything is optional — the dialog sends only what changed. `form` is re-validated on the
 * server against the live version, the same gate a fresh submission passes.
 */
export const editMemberRegistrationSchema = z.object({
  participant: updateParticipantSchema.optional(),
  form: z.record(z.string(), z.unknown()).optional(),
  /** Absent leaves it alone; null clears it — the same rule the participant patch follows. */
  age: z.preprocess((value) => (value === "" ? null : value), ageSchema.nullable().optional()),
});

export type EditMemberRegistrationInput = z.infer<typeof editMemberRegistrationSchema>;

/**
 * One consent, as a public form records it: which kind, whether it was granted, and the version of
 * the wording agreed to. Shared by individual and team self-registration so both surfaces record a
 * consent the same way — the staff path adds `grantedBy`, which a self-registrant has no answer for.
 */
export const consentEntrySchema = z.object({
  type: z.enum(["photo", "media", "liability", "data", "guardian"]),
  granted: z.boolean(),
  /** Which wording was agreed to — the form field key and version, so it stays auditable. */
  textVersion: shortTextSchema,
});

/**
 * One thing a person is entering.
 *
 * `categoryId` is optional because a `placement_only` competition — a horseshoe tournament, canoe
 * races — may have no categories at all. `sessionId` is optional because an entry with no session
 * stands for every session the competition runs.
 */
export const registrationEntryInputSchema = z.object({
  competitionId: ulidSchema,
  categoryId: ulidSchema.optional(),
  sessionId: ulidSchema.optional(),
});

/**
 * A registration: one participant, one event, and the list of what they are entering.
 *
 * Identity is held once and entries hang off it, so three categories across two days is six
 * entries against one registration rather than six registrations (D14).
 */
export const createRegistrationSchema = z
  .object({
    participantId: ulidSchema.optional(),
    participant: participantSchema.optional(),
    entries: z.array(registrationEntryInputSchema).min(1, "Choose at least one thing to enter"),
    formSubmission: z.record(z.string(), z.unknown()).optional(),
    dancerNumber: optionalText(shortTextSchema),
    /**
     * Which desk this is, when an event runs more than one (D86).
     *
     * A property of the device, set once when the desk is set up and sent with everything it
     * registers. Absent is the ordinary case — one desk, numbers allocated across the whole event
     * as they always were.
     */
    station: stationSchema.optional(),
    /** The machine, so two set to the same desk can be told apart. Only read beside `station`. */
    stationDeviceId: deviceIdSchema.optional(),
    /** Recorded on the registration, not on the person — an age is only true of a date. */
    age: optionalAge,
    consents: z
      .array(
        z.object({
          type: z.enum(["photo", "media", "liability", "data", "guardian"]),
          granted: z.boolean(),
          grantedBy: optionalText(shortTextSchema),
          textVersion: shortTextSchema,
        }),
      )
      .default([]),

    /**
     * Registering into a closed window (D14).
     *
     * A dancer arriving at 12:35pm is a person standing at a table, not an error state. Staff with
     * the right permission may proceed; the reason is required and lands in the audit log. Refusing
     * this outright would push the real workflow onto paper and lose the record entirely.
     */
    overrideClosedWindow: z.object({ windowId: ulidSchema, reason: reasonSchema }).optional(),
  })
  .refine((value) => value.participantId != null || value.participant != null, {
    message: "Choose an existing participant or enter a new one",
    path: ["participant"],
  });

export const updateRegistrationSchema = z.object({
  dancerNumber: optionalText(shortTextSchema),
  age: z.preprocess((value) => (value === "" ? null : value), ageSchema.nullable().optional()),
  status: z.enum(["active", "cancelled"]).optional(),
});

export const addEntriesSchema = z.object({
  entries: z.array(registrationEntryInputSchema).min(1),
  overrideClosedWindow: z.object({ windowId: ulidSchema, reason: reasonSchema }).optional(),
});

/**
 * Taking an entry out of a competition, or putting it back.
 *
 * `entered` is in the enum because reversing a withdrawal is the same act asked the other way, and
 * a registrar who withdrew the wrong dancer must be able to undo it where they did it.
 */
export const setEntryStatusSchema = z.object({
  status: z.enum(["entered", "withdrawn", "no_show"]),
  reason: optionalText(reasonSchema),
});

/**
 * Move an entry between the categories of the competition it is already in (D75).
 *
 * `null` takes it out of a category without taking it out of the competition, which is the state
 * an entry the desk has not carded yet is in. Separate from the status patch because they are
 * different acts on different days: one says who is competing, the other says where.
 */
export const setEntryCategorySchema = z.object({
  categoryId: ulidSchema.nullable(),
});

export const CHECK_IN_METHODS = ["qr", "manual", "bulk"] as const;

/**
 * A check-in.
 *
 * The id is supplied by the client so a scan retried after a dropped connection upserts rather
 * than double-counting — and a double count here is money, because participation payouts compute
 * from check-in (D16).
 *
 * The subject is a competitor's entry **or** a role holder's assignment, exactly one of the two
 * (D64). One schema and one batch endpoint rather than two, because it is one act: the gate should
 * not have to know which kind of person it just scanned before deciding where to post it.
 */
export const checkInSchema = z
  .object({
    id: ulidSchema,
    sessionId: ulidSchema,
    registrationEntryId: ulidSchema.optional(),
    roleAssignmentId: ulidSchema.optional(),
    checkedInAt: z.number().int(),
    method: z.enum(CHECK_IN_METHODS).default("qr"),
    deviceId: optionalText(z.string().max(100)),
  })
  .refine(
    (row) => (row.registrationEntryId == null) !== (row.roleAssignmentId == null),
    "A check-in names an entry or a role holder, and exactly one of them",
  );

/** Bulk check-in from a device that was offline. Order is preserved; each row is independent. */
export const checkInBatchSchema = z.object({
  checkIns: z.array(checkInSchema).min(1).max(500),
});

export const reverseCheckInSchema = z.object({
  reason: reasonSchema,
});

/**
 * The code on a participant's check-in QR (D66).
 *
 * Base32 out of `secureToken`, so it is case-insensitive and survives being read aloud when a
 * camera will not focus — which is the whole reason the code is printed under the symbol. Bounded
 * generously rather than pinned to one length: the codes minted today are sixteen characters, and
 * a lookup that refused a longer one would be a migration nobody could deploy.
 */
export const checkInCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .min(12)
  .max(64)
  .regex(/^[0-9A-Z]+$/, "Not a check-in code");

/**
 * A scan: a code held up to a camera, resolved to a person on the server.
 *
 * The gate device knows the session it is standing in and the code it just read, and nothing else —
 * deliberately. Which entries that becomes is the server's answer, because it depends on the event's
 * check-in unit and on what the person actually entered, and a device that computed it locally would
 * be a second implementation of the rule that decides who gets paid (D66).
 *
 * `id` is still client-minted, and is the *first* of however many rows the scan writes; the rest are
 * derived from it, so a retried scan lands on the same ids as the first attempt.
 */
export const scanCheckInSchema = z.object({
  id: ulidSchema,
  code: checkInCodeSchema,
  sessionId: ulidSchema,
  /** Required when the event checks in per competition; ignored when it checks in per session. */
  competitionId: ulidSchema.optional(),
  checkedInAt: z.number().int(),
  deviceId: optionalText(z.string().max(100)),
});

/** The same act from the participant's own phone. The code comes from the path, not the body. */
export const selfCheckInSchema = z.object({
  id: ulidSchema,
  sessionId: ulidSchema,
  competitionId: ulidSchema.optional(),
  checkedInAt: z.number().int(),
});

/**
 * The public, no-account registration (ux.md §3.2).
 *
 * `form` is validated a second time on the server against the *live* form version — this schema
 * only guarantees it is an object. There is deliberately no override field here: the closed-window
 * path belongs to staff at the desk (D14), and a public schema that carried one would make the
 * closing time meaningless.
 */
export const publicRegistrationSchema = z.object({
  participant: participantSchema,
  /** What resolves the age band, and so the suggested category. Optional: not every event asks. */
  age: optionalAge,
  entries: z.array(registrationEntryInputSchema).min(1, "Choose at least one thing to enter"),
  form: z.record(z.string(), z.unknown()).default({}),
  consents: z.array(consentEntrySchema).default([]),
  /** The language the person filled this in, sent by the page that already negotiated it. It
   *  travels onto the confirmation email so the mail is written in the language they were
   *  reading — the only record of that choice, since a participant has no account to hold one.
   *  Unrecognised values fall back to the community's own language at send time. */
  locale: z.string().min(2).max(20).optional(),
});

/** What a participant may change about themselves through `/manage/{token}`: contact details
 *  only. Identity, age and guardian fields are the registrar's to correct. */
export const manageContactPatchSchema = z.object({
  phone: optionalText(phoneSchema),
  email: optionalText(emailSchema),
  address: optionalText(shortTextSchema),
  city: optionalText(shortTextSchema),
  region: optionalText(shortTextSchema),
  postalCode: optionalText(shortTextSchema),
  country: optionalText(shortTextSchema),
  emergencyContactName: optionalText(shortTextSchema),
  emergencyContactPhone: optionalText(phoneSchema),
});

export const manageWithdrawSchema = z.object({
  entryId: ulidSchema,
  reason: optionalText(reasonSchema),
});

export const manageAddEntrySchema = registrationEntryInputSchema;

/** A merge rewrites history across events, so the reason is mandatory (see participant.merge). */
export const mergeParticipantsSchema = z.object({
  duplicateId: ulidSchema,
  reason: reasonSchema,
});

export type ParticipantInput = z.infer<typeof participantSchema>;
export type CreateRegistrationInput = z.infer<typeof createRegistrationSchema>;
export type CheckInInput = z.infer<typeof checkInSchema>;
export type PublicRegistrationInput = z.infer<typeof publicRegistrationSchema>;
